import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@fluentui/react-components";
import { PARTICIPANT_GRID_GAP_PX, getParticipantGridViewportBreakpoints } from "./participant-grid-capacity";
import { ParticipantAdaptiveGrid } from "./ParticipantAdaptiveGrid";
import { ParticipantTile } from "./ParticipantTile";

const useStyles = makeStyles({
  root: {
    position: "relative",
    boxSizing: "border-box",
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0,
    pointerEvents: "auto"
  },
  tileCell: {
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0
  }
});

export function isCompactParticipantGridBreakpoint(breakpoint) {
  return breakpoint === "xs" || breakpoint === "sm" || breakpoint === "md";
}

export function isMobileParticipantGridBreakpoint(breakpoint) {
  return breakpoint === "xs" || breakpoint === "sm";
}

function getParticipantGridWindowViewportBreakpoints() {
  if (typeof window === "undefined") {
    return getParticipantGridViewportBreakpoints({ width: 1200, height: 900 });
  }

  return getParticipantGridViewportBreakpoints({
    width: window.innerWidth || 1200,
    height: window.innerHeight || 900
  });
}

export function ParticipantGridLayout({
  dimensions,
  layout,
  participants,
  totalParticipantCount = participants.length,
  viewportBreakpoints,
  onRequestParticipantFullscreen,
  onRequestParticipantMute,
  onSwipeNextPage,
  onSwipePreviousPage,
  onToggleParticipantMuted,
  onToggleParticipantPinned,
  onToggleParticipantRoomSpotlight
}) {
  const styles = useStyles();
  const gridViewportBreakpoints = viewportBreakpoints || getParticipantGridWindowViewportBreakpoints();
  const hideSingleParticipantControls = totalParticipantCount <= 1;

  return (
    <div className={styles.root}>
      <ParticipantAdaptiveGrid
        className={styles.root}
        dimensions={dimensions}
        disableFloat
        gap={PARTICIPANT_GRID_GAP_PX}
        items={participants}
        onSwipeNextPage={onSwipeNextPage}
        onSwipePreviousPage={onSwipePreviousPage}
        renderItem={({ gridItem, index, item: participant }) => (
          <div
            className={styles.tileCell}
            data-participant-grid-height-breakpoint={gridViewportBreakpoints.height}
            data-participant-grid-index={index}
            data-participant-grid-layout="adaptive"
            data-participant-grid-main={gridItem.isMain ? "true" : "false"}
            data-participant-grid-variant={layout.variant}
            data-participant-grid-width-breakpoint={gridViewportBreakpoints.width}
          >
            <ParticipantTile
              hideFullscreenButton={hideSingleParticipantControls}
              hidePinButton={hideSingleParticipantControls}
              participant={participant}
              slot={layout.slots[index]}
              onRequestParticipantFullscreen={onRequestParticipantFullscreen}
              onRequestParticipantMute={onRequestParticipantMute}
              onToggleParticipantMuted={onToggleParticipantMuted}
              onToggleParticipantPinned={onToggleParticipantPinned}
              onToggleParticipantRoomSpotlight={onToggleParticipantRoomSpotlight}
            />
          </div>
        )}
        swipeEnabled={Boolean(onSwipeNextPage || onSwipePreviousPage)}
      />
    </div>
  );
}

ParticipantGridLayout.propTypes = {
  dimensions: PropTypes.shape({
    height: PropTypes.number,
    width: PropTypes.number
  }),
  layout: PropTypes.shape({
    variant: PropTypes.string.isRequired,
    slots: PropTypes.arrayOf(
      PropTypes.shape({
        index: PropTypes.number.isRequired,
        role: PropTypes.string.isRequired
      })
    ).isRequired
  }).isRequired,
  participants: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired
    })
  ).isRequired,
  totalParticipantCount: PropTypes.number,
  viewportBreakpoints: PropTypes.shape({
    width: PropTypes.string.isRequired,
    height: PropTypes.string.isRequired
  }),
  onToggleParticipantMuted: PropTypes.func.isRequired,
  onRequestParticipantFullscreen: PropTypes.func,
  onRequestParticipantMute: PropTypes.func,
  onSwipeNextPage: PropTypes.func,
  onSwipePreviousPage: PropTypes.func,
  onToggleParticipantPinned: PropTypes.func.isRequired,
  onToggleParticipantRoomSpotlight: PropTypes.func
};
